import { isWithinInterval } from "date-fns";
import { parseDate } from "./dateHelper";

type IPrice = {
	adultPrice: number | null;
	childPrice: number | null;
	groupPrice: number | null;
	studentPrice: number | null;
	seniorPrice: number | null;
	adultFullPrice: number | null;
	childFullPrice: number | null;
	groupFullPrice: number | null;
	studentFullPrice: number | null;
	seniorFullPrice: number | null;
};

function sum(numbers: number[]) {
	return numbers.reduce((a, b) => a + b, 0);
}

const minPrice = ({
	priceTable,
	selectedDateList,
}: {
	priceTable: { [key: string]: IPrice };
	selectedDateList: Date[];
}): IPrice => {
	let prices: IPrice[] = [];
	if (selectedDateList.length > 0) {
		const firstDate = selectedDateList[0];
		const lastDate = selectedDateList[selectedDateList.length - 1];
		Object.keys(priceTable).forEach((dateString) => {
			const date = parseDate(dateString);
			if (isWithinInterval(date, { start: firstDate, end: lastDate })) {
				prices.push(priceTable[dateString]);
			}
		});
	} else {
		prices = prices.concat(Object.values(priceTable));
	}

	// 販売価格が低いもの順に並べるが、販売価格が同じだった場合は、正規料金が高いものを優先する（最安値かつ最大割引率）
	return prices.sort((a: IPrice, b: IPrice) => {
		const aPriceSum = sum([
			a.adultPrice || 0,
			a.childPrice || 0,
			a.groupPrice || 0,
			a.studentPrice || 0,
			a.seniorPrice || 0,
		]);
		const bPriceSum = sum([
			b.adultPrice || 0,
			b.childPrice || 0,
			b.groupPrice || 0,
			b.studentPrice || 0,
			b.seniorPrice || 0,
		]);
		const aFullPriceSum = sum([
			a.adultFullPrice || 0,
			a.childFullPrice || 0,
			a.groupFullPrice || 0,
			a.studentFullPrice || 0,
			a.seniorFullPrice || 0,
		]);
		const bFullPriceSum = sum([
			b.adultFullPrice || 0,
			b.childFullPrice || 0,
			b.groupFullPrice || 0,
			b.studentFullPrice || 0,
			b.seniorFullPrice || 0,
		]);
		if (aPriceSum == bPriceSum) {
			return aFullPriceSum > bFullPriceSum ? -1 : 1;
		} else {
			return aPriceSum > bPriceSum ? 1 : -1;
		}
	})[0];
};

/**
 * 保険料計算の定数とヘルパー関数
 * 保険料の計算ロジックを一元化してDRY原則に従う
 *
 * 注意: この値は SnowItem::INSURANCE_FEE_PER_PERSON と一致させる必要があります
 */
export const INSURANCE_FEE_PER_PERSON = 500;

/**
 * 保険料を計算する
 * @param insuranceApplicable 保険が適用されるかどうか
 * @param forGroup グループ商品かどうか
 * @param maxParticipantsAtOnce 最大参加者数（グループ商品の場合）
 * @param participantCount 参加者数（非グループ商品の場合）
 * @returns 保険料
 */
export function calculateInsuranceFee(
	insuranceApplicable: boolean,
	forGroup: boolean,
	maxParticipantsAtOnce?: number,
	participantCount: number = 1,
): number {
	if (!insuranceApplicable) {
		return 0;
	}

	if (forGroup && maxParticipantsAtOnce) {
		// グループレッスン商品の場合、最大人数分の保険料を適用
		return INSURANCE_FEE_PER_PERSON * maxParticipantsAtOnce;
	} else {
		// 通常の商品の場合、参加者数分の保険料を適用
		return INSURANCE_FEE_PER_PERSON * participantCount;
	}
}

/**
 * 価格に保険料を追加する
 * @param basePrice 基本価格
 * @param insuranceApplicable 保険が適用されるかどうか
 * @param forGroup グループ商品かどうか
 * @param maxParticipantsAtOnce 最大参加者数（グループ商品の場合）
 * @param participantCount 参加者数（非グループ商品の場合）
 * @returns 保険料込みの価格
 */
export function addInsuranceFeeToPrice(
	basePrice: number,
	insuranceApplicable: boolean,
	forGroup: boolean,
	maxParticipantsAtOnce?: number,
	participantCount: number = 1,
): number {
	const insuranceFee = calculateInsuranceFee(
		insuranceApplicable,
		forGroup,
		maxParticipantsAtOnce,
		participantCount,
	);

	return basePrice + insuranceFee;
}

export type ExchangeRate = {
	code: string;
	rate: number;
	symbol: string;
	updatedAt?: string;
};

export function getCurrentExchangeRate(): ExchangeRate | null {
	const element = document.getElementById("exchange-rate-data");
	if (!element?.dataset?.exchangeRate) {
		return null;
	}

	const { exchangeRate } = element.dataset;
	const exchangeRateObject = JSON.parse(exchangeRate);

	return exchangeRateObject;
}

export { minPrice };
